import { Capacitor } from "@capacitor/core";
import { MobileAppFrame } from "@eventr/common-storybook";
import { isIosNativeNavigation } from "feat/nativeNavigation/platform";
import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { useScrollContext } from "state/scrollState/scrollContext";
import { scrollRef } from "state/scrollState/scrollSignals";
import {
  useBackgroundImageUrl,
  useBottomInset,
  useKeyboardHeight,
  useKeyboardOpen,
  useTopInset,
} from "state/windowState/window.store";

/**
 * Pathless layout wrapping every dev-menu screen in the shared `MobileAppFrame`
 * scroll container, so navigating between dev screens does not remount it. The
 * screens themselves are declared in dev.routes.tsx and rendered through the
 * registry, nested under this route.
 *
 * Sized the same way as the main frame in routes.tsx. Each iOS native view locks its
 * document, so a frame taller than the web view (as `100vh` can be) leaves the end of
 * the menu below the screen with nothing able to scroll to it.
 */
export function DevLayout() {
  const topInset = useTopInset();
  const bottomInset = useBottomInset();
  const keyboardOpen = useKeyboardOpen();
  const keyboardHeight = useKeyboardHeight();
  const backgroundImageUrl = useBackgroundImageUrl();

  const [innerHeight, setInnerHeight] = useState(window.innerHeight);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;

    const handler = () => setInnerHeight(window.innerHeight);

    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const nativeNav = isIosNativeNavigation();
  const insetTop = "var(--native-navigation-inset-top, env(safe-area-inset-top, 0px))";
  const insetBottom = "var(--native-navigation-inset-bottom, env(safe-area-inset-bottom, 0px))";
  const frameHeight = keyboardOpen ? innerHeight - keyboardHeight : innerHeight;

  return (
    <MobileAppFrame
      bottomSafeArea={nativeNav ? "0px" : bottomInset + "px"}
      topSafeArea={nativeNav ? "0px" : `${topInset}px`}
      height={frameHeight + "px"}
      width="100%"
      passedRef={scrollRef}
      overscrollBehaviour="contain"
      scrollStyle={{
        scrollBehavior: useScrollContext.getState().scrollBehavior,
        overflowX: "hidden",
        overflowY: "auto",
        // The frame's inner grid otherwise gets an implicit `auto` column, which grows to the
        // min-content of the widest unbreakable child (the logs tag row, a <pre> of JSON) and
        // pushes every dev screen past the right edge, where overflowX clips it.
        gridTemplateColumns: "minmax(0, 1fr)",
        paddingTop: nativeNav ? insetTop : undefined,
        paddingBottom: nativeNav ? insetBottom : undefined,
      }}
      backgroundImageUrl={backgroundImageUrl}
    >
      <Outlet />
    </MobileAppFrame>
  );
}
